// Canonical state hash. Two clients deriving state from the same command log
// must agree on this exactly (PLAN §5.5) — a mismatch is a determinism bug, not
// an attack, and it should halt loudly.
//
// Hash the *canonical serialisation*, never object identity or key order:
// JSON.stringify over a plain object is exactly the trap this avoids.

import type { GameState } from "./commands";
import { rulesetKey } from "./ruleset";
import {
  facingAt,
  KING_FACTORY,
  promotionOwnerAt,
  promotionUsesAt,
  royalsOf
} from "./position";
import {
  beltDirAt,
  beltOnAt,
  blueprintDirAt,
  blueprintOwnerAt,
  beltOwnerAt,
  cookingCount,
  cookingUnitAt,
  handoffAt,
  droppedAt,
  dumpsAt,
  kingActedAtOn,
  kingTaskAt,
  kingTempoAt,
  heapAt,
  depositAt,
  depositYieldAt,
  depositTierAt,
  depositCobbleAt,
  digProgressAt,
  heapRankAt,
  heapSideAt,
  liveSquares,
  loadedAtOn,
  minedAtOn,
  reserveAt,
  mintFacingAt,
  refinerIdAt,
  structureAt,
  structureSquares,
  preachedAtOn,
  targetAt,
  unitAt,
  unitCount
} from "./industry/state";
import { structureHealthOf, structureMaxHealth } from "./industry/structures";
import { trainHash } from "./industry/trains";

/** FNV-1a, 32-bit. Not cryptographic — this detects divergence, not tampering. */
function fnv1a(input: string): number {
  let hash = 0x811c9dc5;
  for (let i = 0; i < input.length; i++) {
    hash ^= input.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return hash >>> 0;
}

/**
 * Every field that can legally differ between two clients, in a fixed order.
 * Append to this — never reorder, or old logs stop reproducing their hashes.
 */
export function canonicalize(state: GameState): string {
  const pos = state.position;
  const ind = state.industry;
  const parts: string[] = [];

  // Live squares, sorted, each tagged with which square it is. Sweeping the
  // whole board would be O(area) per command on an expanded board, and Map
  // order is insertion order — which two clients would agree on only by luck.
  // Sorting the cells that actually hold something is both canonical and
  // cheap, and naming the square is what lets a sparse list mean anything.
  for (const sq of liveSquares(pos.board)) {
    parts.push(
      `${sq}:${(pos.board.get(sq) as number).toString(36)}.${facingAt(pos, sq)}` +
        // Damage taken and a tough piece's health, on the same terms and for
        // the same reason: appended **only when set**, so every board nobody
        // has hit or toughened hashes exactly as it did. Health decides
        // whether a capture is a capture (§27), so two clients that disagree
        // about it disagree about which moves exist — which is a desync that
        // shows up as one of them refusing a move the other just played.
        (pos.hurt.get(sq) ? `.h${pos.hurt.get(sq)}` : "") +
        (pos.vitality.get(sq) ? `.v${pos.vitality.get(sq)}` : "") +
        // Armor, on the same terms: the plate decides when a shot kills.
        (pos.plating.get(sq) ? `.p${pos.plating.get(sq)}` : "") +
        // The variant row, on the same terms: it decides moves and reach, and it
        // is bought with material. Appended only when set.
        (pos.variant.get(sq) ? `.x${pos.variant.get(sq)}` : "")
    );
  }

  parts.push(
    String(pos.turn),
    String(pos.castling),
    String(pos.ep),
    String(pos.halfmove),
    String(pos.fullmove),
    // Both, not just the remainder: two clients that disagree about how many
    // moves a turn holds disagree about whose turn it is one move later.
    `${pos.movesPerTurn}/${pos.movesLeft}/${pos.fodderLeft}`,
    // Who has moved this turn (§33 T1), by body, sorted: it decides which
    // pieces may move next.
    [...pos.turnMoved].sort((a, b) => a - b).join(","),
    // The turn's own bookkeeping (§31 T1–T4): the round, who was alive in it,
    // and what this turn has spent — each decides what is legal next or how
    // many ticks End Turn runs, so two clients must agree on all of them.
    `r${pos.round}.${pos.roundSides}.${pos.turnMoves}.${pos.turnTicks}.${pos.turnForged}`,
    state.status,
    String(state.history.length),
    // The board's extent is state now — it grows — so two clients disagreeing
    // about how big the board is has to show up as a mismatch.
    `${pos.bounds.minFile},${pos.bounds.maxFile},${pos.bounds.minRank},${pos.bounds.maxRank}`
  );

  // Promotion squares and what is left of them. They decide what a pawn's
  // move *does*, and they run out — two clients disagreeing about how many
  // uses a square has left would disagree about the game a move later.
  for (const sq of liveSquares(pos.promotion)) {
    parts.push(`p${sq}:${promotionUsesAt(pos, sq)}`);
  }
  // And whose they are, where a map has said. Its own line rather than a third
  // field on the one above, so a board of unowned squares — every run board —
  // hashes exactly as it did. Swept separately because an owner outlives the
  // uses it was set beside: a spent marker still belongs to somebody.
  for (const sq of liveSquares(pos.promotionOwner)) {
    parts.push(`po${sq}:${promotionOwnerAt(pos, sq)}`);
  }
  // **Whose colours each spy claims, and when the claim runs out** (§26 C12).
  // Hashed on the industry side before, because the wearer is *drawn*
  // differently; here because the spoofed faction may not capture it, so two
  // clients disagreeing about a costume disagree about which moves exist.
  for (const sq of liveSquares(pos.disguise)) {
    const worn = pos.disguise.get(sq)!;
    parts.push(`iff${sq}:${worn.side}.${worn.rank}.${worn.until}`);
  }
  // And whether the far edge promotes at all. Not derivable from the board —
  // it is a property of the *map*, and two clients disagreeing about it would
  // hash identically and then disagree about a pawn's move the first time one
  // reached the edge. Appended only when it is off, so chess is unmoved.
  if (!pos.promotionWalls) parts.push("nowall");
  // Pawns by mode (§31 P2): only when on, so every non-run hash is unmoved.
  if (pos.runPawns) parts.push("runpawn");
  // Artisan Pillars lost (§31 A6): max HP decides which blows kill. Only when any.
  if (pos.frailty.some((n) => n > 0)) parts.push(`frail${pos.frailty.join(".")}`);

  const industrial = liveSquares(
    ind.heap,
    ind.heapSide,
    ind.heapRank,
    ind.deposit,
    ind.depositYield,
    ind.digProgress,
    ind.beltDir,
    ind.blueprintDir,
    ind.beltCargo,
    ind.buffer,
    ind.outBuffer,
    ind.cooking,
    ind.target,
    ind.mintFacing,
    ind.refinerId,
    ind.handoff,
    ind.dump,
    ind.kingTask,
    ind.kingTempo,
    ind.kingLastTask,
    ind.kingActedAt,
    ind.dropped,
    ind.minedAt,
    ind.loadedAt,
    ind.reserve,
    ind.preachedAt
  );

  for (const sq of industrial) {
    parts.push(
      String(sq),
      String(heapAt(ind, sq)),
      // What died here. It is drawn, so two clients disagreeing about it would
      // show two different boards while claiming the same hash.
      String(heapSideAt(ind, sq)),
      String(heapRankAt(ind, sq)),
      String(beltDirAt(ind, sq)),
      String(beltOwnerAt(ind, sq)),
      String(beltOnAt(ind, sq)),
      // A drawn part is a destination material routes toward, so two clients
      // disagreeing about one send the next unit of iron to different places.
      String(blueprintDirAt(ind, sq)),
      String(blueprintOwnerAt(ind, sq)),
      // The whole oven queue, not just the batch nearest done: a king at
      // tempo has several going, and a client that agreed only about the
      // front one would drift on the next tick without the hash noticing.
      String(cookingCount(ind, sq)),
      ...Array.from({ length: cookingCount(ind, sq) }, (_, i) =>
        String(cookingUnitAt(ind, sq, i))
      ),
      String(targetAt(ind, sq)),
      // The crown was hashed here and is hashed with the *variant* now — she
      // is a row, and `hashState` already appends one whenever it is set.
      //
      // A Confessor's cooldown, appended only when she has one, so no board
      // without one hashes differently. Two clients disagreeing about it
      // refuse each other's `empower`.
      preachedAtOn(ind, sq) >= 0 ? `preach${preachedAtOn(ind, sq)}` : "",
      String(mintFacingAt(ind, sq)),
      String(handoffAt(ind, sq))
    );
    // Ore in the ground: what is left in this tile, what it is, and how far
    // into a special somebody has dug.
    //
    // **Pushed only when there is one**, and pushed *outside* the tuple rather
    // than as an empty slot in it: `parts` is joined positionally, so an empty
    // string is still an element and would move every hash on every board that
    // has ever had scrap on it. The rule cell membership follows.
    if (depositAt(ind, sq)) parts.push(`o${depositAt(ind, sq)}.${depositYieldAt(ind, sq)}`);
    // A mixed tile (§31 D1) only, so every pure tile hashes as it did.
    if (depositTierAt(ind, sq)) parts.push(`t${depositTierAt(ind, sq)}.${depositCobbleAt(ind, sq)}`);
    if (digProgressAt(ind, sq)) parts.push(`d${digProgressAt(ind, sq)}`);
    // Units in order, with their provenance masks: two clients must agree on
    // *which* unit sits where, not merely how many.
    for (const store of ["cargo", "in", "out"] as const) {
      const held = unitCount(ind, sq, store);
      parts.push(String(held));
      for (let i = 0; i < held; i++) parts.push(String(unitAt(ind, sq, i, store)));
    }
    parts.push(
      String(refinerIdAt(ind, sq)),
      String(minedAtOn(ind, sq)),
      String(loadedAtOn(ind, sq)),
      String(reserveAt(ind, sq)),
      // A king's job and the tempo it has built on it change what the next
      // tick does, so two clients disagreeing about either is a desync.
      dumpsAt(ind, sq) ? "dump" : "",
      String(kingTaskAt(ind, sq)),
      String(kingTempoAt(ind, sq)),
      String(ind.kingLastTask.get(sq) ?? -1),
      String(kingActedAtOn(ind, sq)),
      // What is lying on the ground. An artifact is a card that has not been
      // collected, so two clients disagreeing about one disagree about a hand.
      `d${droppedAt(ind, sq).join(".")}`
    );
  }

  parts.push(String(ind.turnCounter), String(ind.nextRefinerId));
  // Fuel points (§31 M2), only where a machine holds some — so a board that
  // never burned coal hashes as it always did. Two clients disagreeing about a
  // point disagree about whether the next tick cooks.
  for (const sq of liveSquares(ind.fuel)) parts.push(`fp${sq}:${ind.fuel.get(sq)}`);
  // Zones (§31 D2) and equipment (§31 A0), only where there are any — so a
  // board without either hashes as it always did. Gear by square, never by
  // `pieceId`, which is not hashed.
  if (ind.zone?.size) {
    for (const sq of liveSquares(ind.zone)) parts.push(`z${sq}:${ind.zone.get(sq)}`);
  }
  for (const g of ind.gear ?? []) {
    parts.push(`g${g.kind}.${g.origin}.${g.anchor}.${g.side}.${g.at}` + (g.granted ? `.y${g.granted}` : ""));
  }

  // Appended at the end, never inserted: an old log has to keep reproducing
  // its hash, and every two-player game has no run at all.
  if (state.run) {
    const run = state.run;
    parts.push(
      "run",
      String(run.side),
      String(run.nextCardId),
      run.deck.map((entry) => `${entry.id}:${entry.card}`).join(","),
      run.hand.join(","),
      run.spent.join(","),
      // Banked Free Space tiles. A count a move can spend, so two clients
      // disagreeing about it play different games — the same argument
      // `promotion` uses for its own uses.
      String(run.tiles ?? 0),
      run.level?.levelId ?? "",
      run.revealed.join(","),
      run.objectives.map((one) => `${one.id}:${one.done ? 1 : 0}:${one.progress}`).join(",")
    );
    // Structure tokens in hand (§26 F6). A count a move can spend — `tiles`'
    // argument — and appended only when any are held, so every run saved
    // before tokens existed still hashes the same.
    if (run.tokens && run.tokens.length > 0) {
      parts.push(`tokens${run.tokens.join(",")}`);
    }
    // Hard Mode (§26 F13). It changes despair's grace period, the spawn
    // clock's interval and how strong a camp arrives, so two clients that
    // disagreed about it would play different games from turn one — and being
    // hashed is what puts a saved hard run's mode under the save's own
    // corruption check. Appended only when hard, `tokens`' argument, so every
    // normal run hashes exactly as it did before this existed.
    if (run.mode === "hard") {
      parts.push("mode:hard");
    }
  }
  // Long Socks changes what a king may do and therefore what the position
  // *is*, so it is hashed — but only while somebody has it, which is what
  // keeps every published perft position and every ordinary game byte-identical
  // to what they hashed before the card existed.
  if (pos.socks.size > 0) {
    for (const side of [...pos.socks.keys()].sort((a, b) => a - b)) {
      parts.push(`socks${side}:${pos.socks.get(side)}`);
    }
  }
  for (const sq of liveSquares(pos.armour)) {
    parts.push(`a${sq}:${pos.armour.get(sq)}`);
  }
  // The Static Moat decides which squares a move may cross (§26 C13).
  for (const sq of liveSquares(pos.moat)) {
    parts.push(`m${sq}:${pos.moat.get(sq)}`);
  }
  // Who is allied to whom. It decides what may be taken and what gives check,
  // so two clients disagreeing about it would generate different move lists
  // from the same board. Appended, and only when somebody is actually allied,
  // so every log written before alliances existed still hashes the same.
  if (pos.alliances.some((alliance, side) => alliance !== side)) {
    parts.push(`ally${[...pos.alliances].join("")}`);
  }
  // The seed. Two clients that disagreed about it would generate the same
  // board today and different drops, waves and hands from the next roll
  // onward — a divergence the canary is supposed to catch *before* it shows
  // up as two different games. Appended, and only when it is not the old
  // default, so every log written before anything seeded still hashes the
  // same.
  if (state.seed !== 0) parts.push(`seed${state.seed}`);
  // The round, for the seed's own reason: it decides which camp lands next,
  // which army style it flies and how hard it plays, so
  // two states that agree about the board and disagree about this are two
  // different games from the next boundary onward. **Unlike `tally` it is an
  // input rather than a report**, which is the whole test for what belongs in
  // here — and it cannot defeat threefold repetition the way a climbing counter
  // would, because the repetition key is `repetitionKey(position)` and never
  // this. It is also what makes a saved run's stage covered by the corruption
  // check (`runSave.ts`), which compares this hash. Appended, and only once a
  // front has closed, so every log written before it existed still hashes the
  // same.
  if ((state.stage ?? 0) !== 0) parts.push(`stage${state.stage}`);
  // The ruleset, for the stage's reason: an input to what the rules do, not a
  // report about what they did — two clients disagreeing about which material
  // line is in play derive two different games from one log. Appended only
  // when not the default (the field is only *stored* off the default), so
  // every existing hash and all 25 perft counts are unmoved.
  if (state.ruleset) parts.push(`rules${rulesetKey(state.ruleset)}`);
  // The Hydraulic Clasp: it decides which moves exist, so two clients
  // disagreeing about one would generate different move lists.
  // Appended only while one is up, so every other board hashes as it did. Low
  // square first — the clamp is symmetric, so a-to-b is the same board as b-to-a.
  if (pos.clasp) {
    const c = pos.clasp;
    const [lo, hi] = c.a <= c.b ? [c.a, c.b] : [c.b, c.a];
    parts.push(`clasp${c.side}:${lo},${hi}`);
  }
  // A Confessor's grant. It decides no *move*, but it decides whose move the
  // side must spend next, so two clients disagreeing about one refuse each
  // other's next command — the same divergence a hash exists to catch.
  if (pos.empowered) {
    const e = pos.empowered;
    parts.push(`empower${e.side}:${e.square}:${e.left}`);
  }
  parts.push(`ply${pos.ply}`);
  // Which squares exist, when that is not simply "the whole bounding box".
  //
  // The hash has always recorded `bounds` and never cell *membership*, which
  // was harmless while the only thing that made a board ragged also moved its
  // edges. A structure does not: it takes an interior square out of `cells`
  // and leaves the bounds exactly where they were, so two clients could
  // disagree about where a wall is and hash the same. Appended, and only when
  // the board is ragged, so a classic board and every log written on one still
  // hashes as it did.
  //
  // A rolling checksum rather than the list: `cellList` is thousands of
  // entries on a grown board and this runs on every command.
  const span = pos.bounds.maxFile - pos.bounds.minFile + 1;
  const rows = pos.bounds.maxRank - pos.bounds.minRank + 1;
  if (pos.cells.size !== span * rows) {
    let sum = 0x811c9dc5;
    for (const sq of pos.cellList) sum = Math.imul(sum ^ sq, 0x01000193) >>> 0;
    parts.push(`cells${pos.cells.size}:${sum.toString(36)}`);
  }

  // Which faction each army is. It decides which rules an army plays, so two
  // clients that disagreed would tick different games from one board.
  // Appended, and only when somebody is not a factory army.
  if (pos.kingKind.some((kind) => kind !== KING_FACTORY)) {
    parts.push(`king${[...pos.kingKind].join("")}`);
  }

  // **Which royal is the primary, when a side holds more than one.** `kings`
  // is otherwise derivable from the board — find the king — and so has never
  // been hashed. Coronation breaks that: two kings of one side are both on the
  // board and *the order between them* is state, deciding which is the
  // delivery target and which is promoted when the primary falls. Appended and
  // only when somebody actually has a spare, so every existing log hashes
  // exactly as it did.
  // Borrowed movement patterns (Imitate). Appended and only when somebody has
  // one, so an ordinary board hashes as it always did.
  if (pos.imitating.size > 0) {
    const copied = [...pos.imitating.keys()]
      .sort((a, b) => a - b)
      .map((sq) => `${sq}:${pos.imitating.get(sq)}`)
      .join(",");
    parts.push(`imit${copied}`);
  }

  // Which royals were crowned rather than born, and from what rank.
  //
  // It used to say here that this changes no rule. That was true while a crown
  // was only a *name*; it grants her old pattern now — movement, captures and
  // attacks — so it decides what she reaches and what squares are safe. Two clients
  // deriving one board from one log must agree on what the pieces are called,
  // and a replay that renamed one would be a divergence nothing else reports.
  // Appended and only when somebody has one.
  // Ground on its way back, and how much further it carries. It decides which
  // squares are about to *start* existing, so two clients that disagree about
  // it play different boards a few turns later. Appended, and only while a
  // regrowth runs.
  if (ind.growth.size > 0) {
    const coming = [...ind.growth.keys()]
      .sort((a, b) => a - b)
      .map((sq) => `${sq}:${ind.growth.get(sq)}`)
      .join(",");
    parts.push(`grow${coming}`);
  }

  if (pos.crowned.size > 0) {
    // The rank as well as the square: it decides how she moves and what she
    // takes, so two boards that differ by it are different boards.
    const empresses = [...pos.crowned.keys()]
      .sort((a, b) => a - b)
      .map((sq) => `${sq}:${pos.crowned.get(sq)}`)
      .join(",");
    parts.push(`crown${empresses}`);
  }

  if (pos.extraKings.size > 0) {
    const royal = [...pos.extraKings.keys()]
      .sort((a, b) => a - b)
      .map((side) => `${side}:${royalsOf(pos, side).join(".")}`)
      .join(",");
    parts.push(`royal${royal}`);
  }

  // Each side's flow map, when somebody has one of their own. It decides where
  // every unit of material goes, so two clients that disagreed would route the
  // same board differently and diverge on the first hand-off. Appended and
  // conditional like the line above, so a board where everybody routes the
  // ordinary way — which is every board written before this existed — hashes
  // exactly as it did.
  // Objective squares, when anybody has placed one. A sensor rather than a
  // rule, so nothing about play diverges without it — but two clients that
  // disagreed would point their planners at different ground, which is a
  // different game being played from the same log.
  if (pos.objectives.size > 0) {
    parts.push(`obj${[...pos.objectives].sort((a, b) => a - b).join(",")}`);
  }

  // Standing instructions, on the same terms and for the same reason: a sensor
  // no rule reads, so nothing about play diverges without it, and two clients
  // that disagreed would hand their planners different orders off one log.
  // Nought on every ordinary board, so perft and every existing hash are
  // unmoved.
  if (pos.signals !== 0) parts.push(`sig${pos.signals}`);

  if (pos.demand) {
    parts.push(`flow${[...pos.demand].join(",")}`);
  }

  // Despair, and only when somebody is in it. It decides whether an army is
  // three turns from laying down its arms, so two clients that disagreed would
  // disagree about how many armies are on the board shortly afterwards.
  // Appended and conditional, so every log written before this existed — and
  // every game where nobody has despaired — hashes exactly as it did.
  if (pos.despair.some((at) => at >= 0)) {
    parts.push(`sad${[...pos.despair].join(",")}`);
  }

  // Buildings. What each one is, whose it is, what it is holding and which
  // taps are open — all four decide what the next tick does with the material
  // arriving at it. Sorted by square, never Map order.
  for (const sq of structureSquares(ind)) {
    const one = structureAt(ind, sq)!;
    // And what it has left, **appended only when it has been hit** (§27): the
    // rule `hurt` and `vitality` follow, so every board nobody has swung at
    // hashes exactly as it did. It belongs here rather than in the board sweep
    // above, which walks squares that hold a *piece* — a building's square
    // holds none, so damage recorded there would be outside the hash entirely,
    // and two clients could disagree about whether the next blow takes it.
    const health = structureHealthOf(one);
    parts.push(
      `s${sq}:${one.kind}.${one.owner}.${one.accept}.${one.offer}.${one.paidGrade}` +
        (health < structureMaxHealth(one) ? `.h${health}` : "") +
        // A turret's round (§31 S8), only on a turret.
        (one.turret ? `.t${one.turret.shots},${one.turret.fuel},${one.turret.facing}${one.turret.emplaced ? ",e" : ""}` : "") +
        // Yield Not's bulwark and a House of Cards' count (§31 A11, A12).
        ((one.bulwark ?? 0) > 0 ? `.b${one.bulwark}` : "") +
        ((one.dealt ?? 0) > 0 ? `.d${one.dealt}` : "") +
        // A battery's charge (§31 S6), appended only when it holds some.
        ((one.charge ?? 0) > 0 ? `.c${one.charge}` : "") +
        // A Shield Generator's points this turn and whether it is lit (§31 A9).
        ((one.fed ?? 0) > 0 ? `.f${one.fed}` : "") +
        (one.lit ? ".lit" : "") +
        // An Empower network's extra moves this turn (§33 S1).
        ((one.boosts ?? 0) > 0 ? `.e${one.boosts}` : "") +
        // A train's order, consist and riders (§31 S7), only on a train.
        trainHash(one) +
        `:${one.stock.join(".")}`
    );
  }

  // Who is about to lose a turn. It decides who moves next, which is the
  // loudest disagreement two clients can have about one log — and it is not
  // derivable from the board, since the side it names is standing there looking
  // exactly like a side about to play. Appended, and only when somebody is
  // missing one, so perft and every log written before it existed are unmoved.
  //
  // Read with `?? 0` for `addTally`'s reason: a run saves as a snapshot through
  // a structural codec, so a board written before this field existed comes back
  // without it — and `skipundefined` in here is a hash mismatch, which
  // `runSave.ts` reads as corruption and throws the run away.
  const skip = pos.skipNext ?? 0;
  if (skip !== 0) parts.push(`skip${skip}`);

  // How many extra times each factory runs on its own moves. It decides how
  // much ore comes out of the ground per move, so two clients that disagreed
  // would hold different material a turn later. Appended and conditional, like
  // everything above it, and `withExtraTicks` drops the field when it reaches
  // nought so a run that lost the grant hashes as one that never had it.
  const ticks = state.run?.ticksPerSide;
  // `withExtraTicks` owns the shape — dense, non-negative, dropped at nought —
  // so this joins the array as it stands rather than re-establishing an
  // invariant that has a writer.
  if (ticks?.some((n) => n > 0)) parts.push(`ticks${ticks.join(".")}`);
  // Off-Turn Production (§31 T3), on the same terms and with the same writer shape.
  const offTurn = state.run?.offTurnPerSide;
  if (offTurn?.some((n) => n > 0)) parts.push(`offturn${offTurn.join(".")}`);
  // One-shot points (§31 C4) and trophies (§31 A2): appended, conditional.
  const once = state.run?.offTurnOnce;
  if (once?.some((n) => n > 0)) parts.push(`offturnonce${once.join(".")}`);
  const trophies = state.run?.trophies;
  if (trophies && trophies.length > 0) {
    parts.push(`trophies${trophies.map((t) => `${t.id}:${t.offer.join("+")}`).join(",")}`);
  }
  if (state.run?.nextTrophyId) parts.push(`trophyid${state.run.nextTrophyId}`);
  // The Relic's climb (§31 A5) and the last seizure (§31 A4): a price and a
  // cooldown, so both decide what is legal. Appended, conditional.
  const relics = state.run?.relicsBuilt;
  if (relics?.some((n) => n > 0)) parts.push(`relics${relics.join(".")}`);
  const seizure = state.run?.seizure;
  if (seizure) parts.push(`seize${seizure.by}.${seizure.target}.${seizure.round}.${seizure.at}`);

  return parts.join(",");
}

export function hashState(state: GameState): number {
  return fnv1a(canonicalize(state));
}

export const hashToHex = (hash: number): string => hash.toString(16).padStart(8, "0");
